<template lang="html">
  <div
    v-if="resource"
    :class="[
      center ? 'media--centered' : null,
      resource.columns.background ? 'media--background' : null,
    ]"
    class="media"
  >
    <div v-if="resource.columns" :class="[hasContainer ? 'container' : null]">
      <template v-if="!isVideo">
        <template v-if="!smallResource">
          <div class="media__cms">
            <img
              v-if="resource.columns.file"
              :src="resource.columns.file.url"
              :alt="resource.columns.alt"
              class="media__image"
            >
            <div
              v-if="resource.columns.darkOverlay"
              class="media__dark-overlay"
            />
          </div>
        </template>
        <template v-else>
          <div class="media__picture-wrapper">
            <picture class="media__image">
              <source
                v-if="resource.columns.file"
                :srcset="resource.columns.file.url"
                media="(min-width: 480px)"
              >
              <img
                v-if="smallResource.columns.file"
                :src="smallResource.columns.file.url"
                :alt="smallResource.columns.alt"
              >
            </picture>
            <div
              v-if="resource.columns.darkOverlay"
              class="media__dark-overlay"
            />
          </div>
        </template>
      </template>
      <div v-else class="media__cms media__video-wrapper">
        <video
          ref="video"
          preload="none"
          :controls="isControlled"
          :poster="poster && poster.columns.file.url"
          :muted="isMuted"
          :class="{ 'media__video--pausable': isPausable }"
          class="media__video"
          playsinline
          loop
          :autoplay="isAutoplayed"
        >
          <source
            v-if="hasVideoFile"
            :src="resource.columns.file.url"
            :type="resource.columns.contentType"
          >
        </video>
        <div
          v-if="!isControlled && !isAutoplayed"
          :class="[playing ? 'media__playbutton--playing' : null]"
          class="media__playbutton"
          @click="togglePlayState"
        >
          <component :is="PlayIcon" />
        </div>
      </div>
      <div v-if="resource.columns.description" class="media__meta">
        <div class="media__description-container">
          <span
            class="media__description-item"
            v-html="resource.columns.description"
          />
        </div>
        <span
          v-if="
            resource.columns.description &&
              Array.isArray(!resource.columns.description)
          "
          class="media__description"
          v-html="resource.columns.description"
        />
        <span
          v-if="resource.columns.source"
          class="media__author"
          v-html="resource.columns.source"
        />
      </div>
    </div>
    <slot />
  </div>
</template>

<script>
import { Resource } from "beisheim/models";
import PlayIcon from "beisheim/assets/images/icons/beisheim-stiftung-play.svg";

export default {
  props: {
    container: {
      type: Boolean,
      default: undefined,
    },
    center: {
      type: Boolean,
      default: true,
    },
    autoplay: {
      type: Boolean,
      default: false,
    },
    controls: {
      type: Boolean,
      default: undefined,
    },
    pausable: {
      type: Boolean,
      default: true,
    },
    muted: {
      type: Boolean,
      default: true,
    },
    path: {
      type: String,
      default: () => "",
    },
    small: {
      type: String,
      default: () => "",
    },
  },
  data() {
    return {
      playing: false,
      PlayIcon,
      activeVideoFile: null,
    };
  },
  computed: {
    resource() {
      return Resource.find(this.path);
    },
    smallResource() {
      if (this.small && this.small.length) {
        return Resource.find(this.small);
      }
      return null;
    },
    poster() {
      if (!this.resource.columns.poster) {
        return;
      }
      return Resource.find(this.resource.columns.poster);
    },
    isVideo() {
      return (
        this.resource.columns.video ||
        (this.resource.columns.file &&
          this.resource.columns.file.content_type &&
          this.resource.columns.file.content_type.includes("video"))
      );
    },
    hasVideoFile() {
      const videoFile = Resource.find(this.path).columns.file;
      if (this.activeVideoFile !== videoFile) {
        this.setActiveVideoFile(videoFile);
      }
      return videoFile;
    },
    isAutoplayed() {
      return this.isSet(this.autoplay)
        ? this.autoplay
        : this.resource.columns.autoplay;
    },
    isControlled() {
      return this.isSet(this.controls)
        ? this.controls
        : this.resource.columns.controls;
    },
    isMuted() {
      if (this.isAutoplayed) {
        return true;
      }
      return this.isSet(this.muted) ? this.muted : this.resource.columns.muted;
    },
    isPausable() {
      return this.isSet(this.pausable)
        ? this.pausable
        : this.resource.columns.pausable || true;
    },
    hasContainer() {
      return this.container !== undefined
        ? this.container
        : this.resource.columns.container;
    },
  },
  watch: {
    playing(playing) {
      if (playing) {
        this.$refs.video.play();
      } else {
        this.$refs.video.pause();
      }
    },
    activeVideoFile(activeVideoFile) {
      if (activeVideoFile && this.$refs.video) {
        this.$refs.video.load();
      }
    },
  },
  mounted() {
    if (this.resource && this.resource.columns.video && this.isAutoplayed) {
      this.playing = true;
    }
    const { userAgent } = window.navigator;
    if (userAgent.search(/Trident/i) !== -1) {
      // If internet explorer, execute the picture polyfill on mount,
      // otherwise the polyfill is only working on a hard reload.
      window.picturefill();
    }
  },
  methods: {
    isSet(prop) {
      return prop !== undefined && prop !== null;
    },
    togglePlayState() {
      this.playing = !this.playing;
    },
    setActiveVideoFile(videoFile) {
      this.activeVideoFile = videoFile;
    },
  },
};
</script>

<style lang="scss">
@import "beisheim/scss/grid";
@import "beisheim/scss/scale";
@import "beisheim/scss/breakpoints";
@import "beisheim/scss/font";
@import "beisheim/scss/colors";

.media {
  position: relative;

  &--centered {
    margin: 0 auto;
  }

  @include to(2) {
    &:nth-child(2n + 1) {
      .media__meta {
        margin-left: scale(2);
      }
    }
  }

  &__dark-overlay {
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    background: rgba(0, 0, 0, 0.2);
  }

  &__description-container {
    display: flex;
    justify-content: space-between;
    flex-direction: row;
  }

  .media__playbutton {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    cursor: pointer;
    transition: opacity 150ms ease-in-out;
    background: rgba(0, 0, 0, 0.3);

    svg {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    &--playing {
      opacity: 0;
    }
  }

  &__picture-wrapper {
    position: relative;
  }

  &__video-wrapper {
    position: relative;
  }

  &__image,
  &__video {
    position: relative;
    display: block;
    width: 100%;
    line-height: 0;

    img {
      width: 100%;
      max-width: 100%;
    }
  }

  &__description {
    display: inline;
    margin-right: 5px;
  }

  &__author {
    display: inline;
    font-size: font-size(-1);
    color: gray(2);
    white-space: nowrap;
  }

  &__video {
    pointer-events: none;
  }

  &__video--pausable {
    pointer-events: auto;
  }

  &--background {
    background-color: gray(4);
  }

  &__meta {
    padding-top: scale(3);
    padding-bottom: scale(3);
  }

  &--margin-small {
    margin-top: scale(6);
    margin-bottom: scale(6);
  }

  &--margin-medium {
    margin-top: scale(18);
    margin-bottom: scale(18);
  }

  &--margin-large {
    margin-top: scale(36);
    margin-bottom: scale(36);
  }
}
</style>
